'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tabs,
    TabsList,
    TabsTrigger,
} from '@quadminds/mindkit';
import { ANALIZE_STATUS } from '@quadminds/mindy-app/enums/analize.enum';
import type { FileStatus } from '@quadminds/mindy-app/types/File/File.types';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FileFiltersProps {
    activeFilter: boolean | null;
    statusFilter: FileStatus | null;
    onActiveFilterChange: (value: boolean | null) => void;
    onStatusFilterChange: (value: FileStatus | null) => void;
}

export function FileFilters({
    activeFilter,
    statusFilter,
    onActiveFilterChange,
    onStatusFilterChange,
}: FileFiltersProps) {
    const { t } = useTranslation();
    // Convertir los valores de filtro a strings para los componentes
    const getActiveValue = () => {
        if (activeFilter === null) return 'all';
        return activeFilter ? 'active' : 'inactive';
    };

    const getStatusValue = () => {
        if (statusFilter === null) return 'all';
        return statusFilter;
    };

    // Convertir los valores de string a los tipos correctos
    const handleActiveChange = (value: string) => {
        if (value === 'all') onActiveFilterChange(null);
        else if (value === 'active') onActiveFilterChange(true);
        else onActiveFilterChange(false);
    };

    const handleStatusChange = (value: string) => {
        if (value === 'all') onStatusFilterChange(null);
        else onStatusFilterChange(value as FileStatus);
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap w-full justify-center lg:justify-start items-center gap-2">
                {/* Filtro de estado activo - Versión móvil */}
                <div className="lg:hidden w-full">
                    <Select value={getActiveValue()} onValueChange={handleActiveChange}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('knowledgeManager.filters.all')}</SelectItem>
                            <SelectItem value="active">{t('knowledgeManager.filters.actived')}</SelectItem>
                            <SelectItem value="inactive">{t('knowledgeManager.filters.notActived')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Filtro de estado activo - Versión escritorio */}
                <div className="hidden lg:block">
                    <Tabs value={getActiveValue()} onValueChange={handleActiveChange} className="w-fit">
                        <TabsList data-testid="filter-by-enabled">
                            <TabsTrigger value="all" data-testid="filter-all">
                                {t('knowledgeManager.filters.all')}
                            </TabsTrigger>
                            <TabsTrigger value="active" className="gap-1" data-testid="filter-enable">
                                {t('knowledgeManager.filters.actived')}
                            </TabsTrigger>
                            <TabsTrigger value="inactive" className="gap-1" data-testid="filter-not-enable">
                                {t('knowledgeManager.filters.notActived')}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {/* Filtro de estado de análisis - Versión móvil */}
                <div className="lg:hidden w-full">
                    <Select value={getStatusValue()} onValueChange={handleStatusChange}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Análisis" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('knowledgeManager.filters.all')}</SelectItem>
                            {Object.values(ANALIZE_STATUS).map((status) => (
                                <SelectItem value={status}>{t(`knowledgeManager.filters.${status}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Filtro de estado de análisis - Versión escritorio */}
                <div className="hidden lg:block">
                    <Tabs value={getStatusValue()} onValueChange={handleStatusChange} className="w-fit">
                        <TabsList data-testid="filter-by-status">
                            <TabsTrigger value="all" data-testid="filter-by-status-all">
                                {t('knowledgeManager.filters.all')}
                            </TabsTrigger>
                            {Object.values(ANALIZE_STATUS).map((status) => (
                                <TabsTrigger
                                    key={status}
                                    value={status}
                                    className="gap-1"
                                    data-testid={`filter-by-status-${status}`}
                                >
                                    {t(`knowledgeManager.filters.${status}`)}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Botón para limpiar todos los filtros */}
            {(activeFilter !== null || statusFilter !== null) && (
                <Button
                    variant="ghost"
                    aria-label={t('knowledgeManager.filters.clear')}
                    onClick={() => {
                        onActiveFilterChange(null);
                        onStatusFilterChange(null);
                    }}
                    className="h-8 px-2"
                >
                    <X className="h-4 w-4 mr-1" />
                    {t('knowledgeManager.filters.clear')}
                </Button>
            )}
        </div>
    );
}

export default FileFilters;
